/**
 * Overlay for the encounterInfo fields the API owns (see buildVisitForm).
 * Spread this LAST in any whole-blob encounterInfo write.
 *
 * surveysToDocument: a non-empty live map carries user toggles (written to
 * Yjs only, never reflected in visitForm). Merge per key — live toggles win,
 * merged-only keys come through — because taking the live snapshot wholesale
 * would hide surveys completed after the last toggle.
 *
 * soapNoteDetailPreference: nothing in V2 writes it to Yjs, so a missing
 * live value falls back to the merged view.
 */
export const overlayApiAuthoritativeEncounterInfo = (
  live: Record<string, any>,
  merged: Record<string, any>
): Record<string, any> => ({
  surveysToDocument:
    live?.surveysToDocument && Object.keys(live.surveysToDocument).length > 0
      ? { ...merged?.surveysToDocument, ...live.surveysToDocument }
      : merged?.surveysToDocument,
  soapNoteDetailPreference: live?.soapNoteDetailPreference ?? merged?.soapNoteDetailPreference,
});

/**
 * The standard whole-blob encounterInfo write: merged base, live Yjs wins,
 * API-authoritative overlay last, optional complaints override. Use this
 * instead of hand-spreading the layers.
 */
export const mergeLiveEncounterInfo = (
  live: Record<string, any>,
  merged: Record<string, any>,
  complaints?: any[]
): Record<string, any> => {
  // Same visitTimes rule as buildVisitForm: an empty live array is a stale
  // Yjs seed unless the user explicitly cleared the schedule, so keep the
  // merged (API) times and let whole-blob writes heal the doc.
  const liveVisitTimesPopulated = Array.isArray(live?.visitTimes) && live.visitTimes.length > 0;
  const healVisitTimes =
    !liveVisitTimesPopulated &&
    live?.visitTimesClearedByUser !== true &&
    merged?.visitTimes !== undefined;

  return {
    ...merged,
    ...live,
    ...overlayApiAuthoritativeEncounterInfo(live, merged),
    ...(healVisitTimes ? { visitTimes: merged.visitTimes } : {}),
    ...(complaints !== undefined ? { complaints } : {}),
  };
};
